import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Room, RoomEvent, Track, VideoPresets, ConnectionState } from 'livekit-client';
import { toggleGlobalLivekitMic } from '../../store/GlobalLivekitSlice/GlobalLivekitSlice';
import { useMicrophoneTrack } from '../../../../hooks/useMicrophoneTrack';
import { getGlobalLivekitToken } from '../../store/GlobalLivekitSlice/GlobalLivekitThunk';
import { useRemoteAudioTracks } from '../../hooks/useRemoteAudioTracks';
import AudioPlayback from '../AudioPlayback/AudioPlayback';

export default function LivekitGlobalRoomComponent() {

    //INIT FOR GLOBAL ROOM
    const dispatch = useDispatch();
    const initRef = useRef(false);
    useEffect(() => {
        if (!initRef.current) {
            initRef.current = true;
            dispatch(getGlobalLivekitToken());
        }
    }, []);

    // Livekit URL
    const {
        livekitUrl,
    } = useSelector((state) => state.auth);

    // Redux state
    const {
        globalLivekitMic,
        globalLivekitToken
    } = useSelector((state) => state.globalLivekit);

    const globalLivekitMicRef = useRef(globalLivekitMic);

    useEffect(() => {
        globalLivekitMicRef.current = globalLivekitMic;
    }, [globalLivekitMic])

    useEffect(() => {
        console.log('Token:', globalLivekitToken, 'URL:', livekitUrl);
    }, [globalLivekitToken, livekitUrl]);


    const {
        selectedMic,
    } = useSelector((state) => state.mediaControls);

    const roomGlobal = useMemo(
        () =>
            new Room({
                adaptiveStream: true,
                dynacast: true,
                publishDefaults: {
                    videoCodec: "vp9",
                },
                videoCaptureDefaults: {
                    resolution: VideoPresets.h720.resolution,
                }
            }),
        [],
    );

    //Connection state
    const [globalConnectionError, setGlobalConnectionError] = useState(null);


    //Microphone Audio Track
    const { track: globalAudioTrack } = useMicrophoneTrack(selectedMic, globalLivekitMic);


    //global room connected checker
    const isGlobalRoomConnected = useCallback(() => {
        return roomGlobal.state === ConnectionState.Connected;
    }, [roomGlobal]);

    //Global Remote Audio Track
    const globalRemoteAudioTracks = useRemoteAudioTracks(roomGlobal);


    const cleanupGlobalTracksBySource = useCallback(async (sourceType, excludeTrackSid = null) => {
        if (!roomGlobal?.localParticipant) return;

        const tracksToUnpublish = [];
        const publicationsToCheck = sourceType === Track.Source.Microphone
            ? roomGlobal.localParticipant.audioTrackPublications
            : roomGlobal.localParticipant.videoTrackPublications;

        publicationsToCheck.forEach((pub) => {
            if (pub.source === sourceType && pub.trackSid !== excludeTrackSid && pub.track) {
                tracksToUnpublish.push(pub.track);
            }
        });

        if (tracksToUnpublish.length > 0) {
            console.log(`Cleaning up ${tracksToUnpublish.length} old ${sourceType} track(s)`);
            try {
                await roomGlobal.localParticipant.unpublishTracks(tracksToUnpublish);

                // Stop the tracks after unpublishing
                tracksToUnpublish.forEach(track => {
                    //Don't stop camera track to keep effect alive
                    if (sourceType !== Track.Source.Camera) {
                        track.stop();
                        console.log(`Stopped track: ${track.sid}`);
                    }
                });
            } catch (error) {
                console.error(`Error cleaning up old ${sourceType} tracks:`, error);
            }
        }
    }, [roomGlobal]);

    // Room connection and event listeners
    useEffect(() => {
        if (!globalLivekitToken || !livekitUrl || !roomGlobal) return;

        const handleLocalTrackUnpublished = (publication, participant) => {
            console.log(`Track unpublished: ${publication.kind}, source: ${publication.source}, sid: ${publication.trackSid}`);
        };
        const onConnected = () => {
            console.log('Connected to Global LiveKit room');
            setGlobalConnectionError(null);
        }
        const onDisconnected = () => {
            console.log('Disconnected from Global LiveKit room');
        }
        const onConnectionQualityChanged = (quality, participant) => {
            console.log('Connection quality changed:', quality, participant);
        }

        const connectGlobalToRoom = async () => {
            // Set up event listeners before connecting
            roomGlobal.on(RoomEvent.Connected, onConnected);

            roomGlobal.on(RoomEvent.Disconnected, onDisconnected);

            roomGlobal.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
            roomGlobal.on(RoomEvent.ConnectionQualityChanged, onConnectionQualityChanged);

            // Connect to the room
            await roomGlobal.connect(livekitUrl, globalLivekitToken);
            console.log("Connected to Global LiveKit room: ", roomGlobal.getSid());

        };

        try {
            connectGlobalToRoom();
        } catch (error) {
            console.error('Failed to connect to Global LiveKit room:', error);
            setGlobalConnectionError(error.message);
        }
        // Cleanup function
        return () => {
            console.log("Cleaning up room connection...");
            roomGlobal.off(RoomEvent.Connected, onConnected);
            roomGlobal.off(RoomEvent.Disconnected, onDisconnected);
            roomGlobal.off(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
            roomGlobal.off(RoomEvent.ConnectionQualityChanged, onConnectionQualityChanged);
            roomGlobal.disconnect();
        };
    }, [globalLivekitToken, livekitUrl, roomGlobal, cleanupGlobalTracksBySource]);

    useEffect(() => {
        if (!roomGlobal || !isGlobalRoomConnected()) {
            dispatch(toggleGlobalLivekitMic(false));
            console.error("Global room not connected, skipping audio track management");
            return;
        }

        const manageGlobalAudioTrack = async () => {

            if (globalLivekitMic && globalAudioTrack) {
                console.log('Publishing global audio track');

                // Proactive cleanup before publishing
                await cleanupGlobalTracksBySource(Track.Source.Microphone);

                try {
                    await roomGlobal.localParticipant.publishTrack(globalAudioTrack, {
                        name: 'global-microphone',
                        source: Track.Source.Microphone
                    });

                    console.log('Global audio track published successfully');

                } catch (error) {
                    console.error('Failed to publish global audio track:', error);
                    dispatch(toggleGlobalLivekitMic(false));
                }
            } else if (!globalLivekitMic) {
                console.log('Turning off global mic');
                await cleanupGlobalTracksBySource(Track.Source.Microphone);
            } else {
                console.log('Global mic enabled but no audio track available');
                console.log('This might indicate the microphone hook failed to create a track');
            }
        };
        try {
            manageGlobalAudioTrack();
        }
        catch (error) {
            console.error('Error in manageGlobalAudioTrack:', error);
        }

    }, [roomGlobal, globalLivekitMic, globalAudioTrack, isGlobalRoomConnected, cleanupGlobalTracksBySource, dispatch]);

    useEffect(() => {
        console.log('Global Audio Track:', globalAudioTrack);
    }, [globalAudioTrack]);

    return (
        <>
            <div className="livekit-room-component" style={{ display: 'none' }}>
                {/* Debug information (can be removed in production) */}
                {process.env.NODE_ENV === 'development' && (
                    <div>
                        <div>Status: {isGlobalRoomConnected() ? 'Connected' : 'Disconnected'}</div>
                        <div>Room State: {roomGlobal?.state}</div>
                        {globalConnectionError && <div>Error: {globalConnectionError}</div>}
                        <div>
                            Audio Tracks: {roomGlobal?.localParticipant?.audioTrackPublications.size || 0}
                        </div>
                    </div>
                )}

                {/* Hidden elements for camera effects processing */}


            </div>

            {/* Audio playback for remote audio tracks */}
            <AudioPlayback remoteAudioTracks={globalRemoteAudioTracks} />

        </>
    );
}
